import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet";
import { Container, Col, Row, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import useI18n from "../../hooks/useI18n";
import useEnv from "../../hooks/useEnv";
import { CUSTOMERTYPE } from "../../contexts/AuthContext";

// components
import AlertDialog from "../../components/AlertDialog";
import LocaleSelector from "../../components/LocaleSelector";
import Logo from "../../components/Logo";
import LocaleDropdownSelector from "../../components/LocaleDropdownSelector";
import Footer from "../../components/Footer";
import useUserAccount from "../../hooks/useUserAccount";

// Paragraphs to be translated
const messages = defineMessages({
    welcomeMessage: {
        id: "Tervetuloa MyRopoon!",
        defaultMessage: "Tervetuloa MyRopoon!"
    },
    serviceDescription: {
        id: "MyRopossa voit hoitaa laskuihin ja myöhästyneisiin maksuihin liittyviä asioita helposti ja turvallisesti sähköisen tunnistautumisen avulla.",
        defaultMessage: "MyRopossa voit hoitaa laskuihin ja myöhästyneisiin maksuihin liittyviä asioita helposti ja turvallisesti sähköisen tunnistautumisen avulla."
    },
    loginperson: {
        id: "Kirjaudu henkilöasiakkaana",
        defaultMessage: "Kirjaudu henkilöasiakkaana"
    },
    logincompany: {
        id: "Kirjaudu yritysasiakkaana",
        defaultMessage: "Kirjaudu yritysasiakkaana"
    },
    login: {
        id: "Kirjaudu MyRopo-palveluun",
        defaultMessage: "Kirjaudu MyRopo-palveluun"
    }
});

export { messages };

const Login = (props) => {
    const lng = props.intl.formatMessage;
    const location = useLocation();
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('light');
    const { getLocale } = useI18n();
    const { userIdentityVerificationUrl } = useUserAccount();
    const userLocale = getLocale();
    const { isEnvTruthy } = useEnv();

    useEffect(() => {
        // make sure that chat is not visible in the login page
        removeLivechat();

        // check if alert is passed in location props 
        if (location?.state?.alert) {
            setAlertMessage(location.state.alert.message);
            setAlertType(location.state.alert.type);
            setShowAlert(true);
        }
    }, [location]);

    /**
     * Remove livechat script and the chat widget container,
     * because chat should not be usable in login page.
     */
    const removeLivechat = () => {
        // remove chat script
        const scripts = document.getElementsByTagName('script');
        const url = ('https:' === document.location.protocol ? 'https://' : 'http://') + 'cdn.livechatinc.com/tracking.js';

        if (scripts[0]?.src !== undefined && (scripts[0]?.src).includes(url)) {
            document.head.removeChild(scripts[0]);
        }

        // remove the whole chat widget container
        if (document.getElementById('chat-widget-container')) {
            document.getElementById('chat-widget-container').remove();
        }
    }

    const userIdSignInButtonsElement = <>
        <Link
            className="btn login-button justify-content-center align-items-center p-3"
            to={{
                pathname: '/user/signin',
                search: `?customerType=${CUSTOMERTYPE.PERSON}`
            }}
            aria-label={lng(messages.loginperson)}
        >
            {lng(messages.loginperson)}
        </Link>
        {isEnvTruthy('COMPANY_TOGGLE') && (
            <Link
                className="btn login-button justify-content-center align-items-center p-3"
                to={{
                    pathname: '/user/signin',
                    search: `?customerType=${CUSTOMERTYPE.COMPANY}`
                }}
                aria-label={lng(messages.logincompany)}
            >
                {lng(messages.logincompany)}
            </Link>
        )}
    </>

    return (
        <Container id="login" className="login viewport" fluid>
            <Helmet>
                <title>{lng(messages.login)} | MyRopo</title>
            </Helmet>
            <Row className="login-header" >
                <Col xs={12} className="d-none d-lg-block">
                    <LocaleSelector
                        colClassnames={"d-inline-flex"}
                        optionClassnames={"login-language btn btn-login-language"}
                        selected={userLocale}
                        {...props} />
                </Col>
                {/* Language selection as dropdown in mobile */}
                <Col xs={12} className="d-lg-none">
                    <LocaleDropdownSelector />
                </Col>
            </Row>
            <Row className="login-content">
                <Col xs={{ span: 12, offset: 0 }} lg={{ span: 4, offset: 8 }} className="">
                    <div className="mb-4">
                        <Logo width={251} height={66} color='white' />
                    </div>
                    <div className="login-text mb-4">
                        <p>
                            <b>
                                {lng(messages.welcomeMessage)}
                            </b>
                        </p>
                        <p>
                            {lng(messages.serviceDescription)}
                        </p>
                    </div>
                    {showAlert && (
                        <AlertDialog
                            message={alertMessage}
                            type={alertType}
                        />
                    )}
                    <div className="d-grid gap-3">
                        {isEnvTruthy('USER_ID_SIGN_IN') ? (
                            userIdSignInButtonsElement
                        ) : (<> {isEnvTruthy('COMPANY_TOGGLE') ? (
                            <>
                                <Button
                                    className="login-button"
                                    variant="primary"
                                    href={userIdentityVerificationUrl()}
                                    aria-label={lng(messages.loginperson)}
                                >
                                    <span className="login-button-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={far.faUser} />
                                    </span>
                                    <span id="loginperson" className="login-button-desc" >
                                        {lng(messages.loginperson)}
                                    </span>
                                </Button>
                                <Button
                                    id="logincompany"
                                    className="login-button"
                                    variant="primary"
                                    href={userIdentityVerificationUrl(true)}
                                    aria-label={lng(messages.logincompany)}
                                >
                                    <span className="login-button-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={far.faUsers} />
                                    </span>
                                    <span className="login-button-desc">
                                        {lng(messages.logincompany)}
                                    </span>
                                </Button>
                            </>
                        ) : (
                            <Button
                                id="logindefault"
                                className="login-button"
                                variant="primary"
                                href={userIdentityVerificationUrl()}
                            >
                                <span className="login-button-icon">
                                    <FontAwesomeIcon icon={far.faUser} />
                                </span>
                                <span id="loginperson" className="login-button-desc">
                                    {lng(messages.login)}
                                </span>
                            </Button>
                        )}
                        </>)
                        }
                    </div>
                </Col>
            </Row>
            <Footer class="login-footer"/>
        </Container>
    );
};

Login.propTypes = {
    'intl': PropTypes.object.isRequired
};

export default injectIntl(Login);
